import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { SALES_MANAGE_ROLES, hasRole } from '@/auth/roles';
import { getBranches, getGrades, getSchools } from '@/api/lookups.api';
import { createLead } from '@/api/leads.api';
import { ApiError, type ApiErrorDetail } from '@/api/errors';
import { Banner } from '@/components/common/Banner';
import { FormField } from '@/components/form/FormField';
import { LookupSelect } from '@/components/form/LookupSelect';
import { AgentCombobox } from '@/components/form/AgentCombobox';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { resolveSingleBranch } from '@/lib/branchScope';
import { LEAD_SOURCES } from '@/types/shared';
import type { LeadSource, LookupItem } from '@/types/shared';
import './LeadFormPage.css';

interface FormState {
  fullName: string;
  phone: string;
  secondaryPhone: string;
  email: string;
  gradeInterestedId: number | '';
  schoolInterestedId: number | '';
  leadSource: LeadSource | '';
  assignedEmployee: string;
  /** T175 (§20, DEC-BR10) — required when the creating user is multi/all-branch; omitted (hidden)
   * for a single-branch caller, whose branch the server defaults implicitly. */
  branchId: number | '';
  note: string;
}

const EMPTY_FORM: FormState = {
  fullName: '',
  phone: '',
  secondaryPhone: '',
  email: '',
  gradeInterestedId: '',
  schoolInterestedId: '',
  leadSource: '',
  assignedEmployee: '',
  branchId: '',
  note: '',
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

export function LeadFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAssignOnCreate = hasRole(user?.role, SALES_MANAGE_ROLES);

  // T175 (§20, DEC-BR10, `docs/design/19-branch-scoping-ux.md` §3.1) — same rule as the student
  // registration form: a single-branch caller never sees the field, everyone else gets a required
  // select narrowed to their own scope.
  const singleBranch = resolveSingleBranch(user);
  const showBranchField = !singleBranch;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [grades, setGrades] = useState<LookupItem[]>([]);
  const [schools, setSchools] = useState<LookupItem[]>([]);
  const [branches, setBranches] = useState<LookupItem[]>([]);
  const [initializing, setInitializing] = useState(true);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  useEffect(() => {
    let cancelled = false;
    setInitializing(true);
    Promise.all([
      getGrades({ activeOnly: false }),
      getSchools({ activeOnly: false }),
      getBranches({ activeOnly: false }),
    ])
      .then(([gradeItems, schoolItems, branchItems]) => {
        if (cancelled) return;
        setGrades(gradeItems);
        setSchools(schoolItems);
        setBranches(branchItems);
      })
      .catch(() => {
        if (!cancelled) {
          setGrades([]);
          setSchools([]);
          setBranches([]);
        }
      })
      .finally(() => {
        if (!cancelled) setInitializing(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function validate(): FieldErrors {
    const errors: FieldErrors = {};
    if (form.fullName.trim().length < 3) errors.fullName = t('leads.form.errors.fullNameRequired');
    if (!form.phone.trim()) errors.phone = t('leads.form.errors.phoneRequired');
    if (!form.leadSource) errors.leadSource = t('leads.form.errors.leadSourceRequired');
    if (showBranchField && form.branchId === '') {
      errors.branchId = t('leads.form.errors.branchRequired');
    }
    // assignedEmployee is now a select of valid Sales Agents — no free-text UUID to validate.
    return errors;
  }

  function applyServerFieldErrors(details: ApiErrorDetail[]) {
    const errors: FieldErrors = {};
    for (const detail of details) {
      if (detail.field && detail.field in EMPTY_FORM) {
        errors[detail.field as keyof FormState] = detail.message;
      }
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...errors }));
      const firstKey = Object.keys(errors)[0];
      document.getElementById(firstKey)?.focus();
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBanner(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstKey = Object.keys(errors)[0];
      document.getElementById(firstKey)?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const created = await createLead({
        fullName: form.fullName.trim(),
        phone: form.phone.trim(),
        secondaryPhone: form.secondaryPhone.trim() || undefined,
        email: form.email.trim() || undefined,
        gradeInterestedId: form.gradeInterestedId === '' ? undefined : form.gradeInterestedId,
        schoolInterestedId: form.schoolInterestedId === '' ? undefined : form.schoolInterestedId,
        leadSource: form.leadSource as LeadSource,
        assignedEmployee:
          canAssignOnCreate && form.assignedEmployee.trim()
            ? form.assignedEmployee.trim()
            : undefined,
        // CREATE-only, same omit-for-single-branch convention as the student registration form
        // (`showBranchField` is false for a single-branch caller — the server defaults it, FR-18).
        branchId: showBranchField && form.branchId !== '' ? form.branchId : undefined,
        note: form.note.trim() || undefined,
      });
      const message = t('leads.form.createSuccess', { name: created.fullName });
      showSuccessToast(message);
      navigate(`/leads/${created.leadId}`, {
        state: { successMessage: message },
      });
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      if (apiError.code === 'NETWORK_ERROR') {
        const message = t('leads.form.networkError');
        setBanner(message);
        showErrorToast(message);
      } else if (apiError.code === 'ASSIGNEE_NOT_SALES_AGENT') {
        setFieldErrors((prev) => ({
          ...prev,
          assignedEmployee: t('leads.form.errors.assigneeNotSalesAgent'),
        }));
      } else if (apiError.code === 'BRANCH_REQUIRED' || apiError.code === 'BRANCH_OUT_OF_SCOPE') {
        setFieldErrors((prev) => ({
          ...prev,
          branchId:
            apiError.code === 'BRANCH_REQUIRED'
              ? t('leads.form.errors.branchRequired')
              : t('leads.form.errors.branchOutOfScope'),
        }));
        document.getElementById('branchId')?.focus();
      } else {
        setBanner(apiError.message || t('leads.form.serverError'));
        if (apiError.details.length > 0) applyServerFieldErrors(apiError.details);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (initializing) {
    return <p className="fa-form-loading">{t('common.loading')}</p>;
  }

  return (
    <section className="fa-lead-form">
      <Link to="/leads" className="fa-back-link">
        <span className="fa-back-link__arrow" aria-hidden="true" />
        {t('leads.detail.backToList')}
      </Link>
      <h1 className="fa-lead-form__title">{t('leads.form.createTitle')}</h1>

      {banner && (
        <Banner tone="danger" assertive>
          {banner}
        </Banner>
      )}

      <p className="fa-required-legend">
        <span className="fa-field__required" aria-hidden="true">
          *
        </span>
        {t('leads.form.requiredLegend')}
      </p>

      <form noValidate onSubmit={handleSubmit}>
        <section className="fa-form-section">
          <h2 className="fa-form-section__title">{t('leads.form.contactSection')}</h2>

          <div className="fa-field-row">
            <FormField
              id="fullName"
              label={t('leads.form.fullName')}
              required
              error={fieldErrors.fullName}
            >
              <input
                type="text"
                value={form.fullName}
                disabled={submitting}
                onChange={(e) => setField('fullName', e.target.value)}
              />
            </FormField>
            <FormField id="phone" label={t('leads.form.phone')} required error={fieldErrors.phone}>
              <input
                type="tel"
                className="fa-ltr-isolate"
                value={form.phone}
                disabled={submitting}
                onChange={(e) => setField('phone', e.target.value)}
              />
            </FormField>
          </div>

          <div className="fa-field-row">
            <FormField id="secondaryPhone" label={t('leads.form.secondaryPhone')}>
              <input
                type="tel"
                className="fa-ltr-isolate"
                value={form.secondaryPhone}
                disabled={submitting}
                onChange={(e) => setField('secondaryPhone', e.target.value)}
              />
            </FormField>
            <FormField id="email" label={t('leads.form.email')}>
              <input
                type="email"
                className="fa-ltr-isolate"
                value={form.email}
                disabled={submitting}
                onChange={(e) => setField('email', e.target.value)}
              />
            </FormField>
          </div>
        </section>

        <section className="fa-form-section">
          <h2 className="fa-form-section__title">{t('leads.form.interestSection')}</h2>

          <div className="fa-field-row">
            {showBranchField && (
              <FormField
                id="branchId"
                label={t('leads.form.branch')}
                required
                error={fieldErrors.branchId}
              >
                <LookupSelect
                  id="branchId"
                  value={form.branchId}
                  onChange={(v) => setField('branchId', v)}
                  items={branches}
                  placeholder={t('leads.form.branchPlaceholder')}
                  disabled={submitting}
                />
              </FormField>
            )}
            <FormField id="gradeInterestedId" label={t('leads.form.gradeInterested')}>
              <LookupSelect
                id="gradeInterestedId"
                value={form.gradeInterestedId}
                onChange={(v) => setField('gradeInterestedId', v)}
                items={grades}
                placeholder={t('leads.form.gradePlaceholder')}
                disabled={submitting}
              />
            </FormField>
            <FormField id="schoolInterestedId" label={t('leads.form.schoolInterested')}>
              <LookupSelect
                id="schoolInterestedId"
                value={form.schoolInterestedId}
                onChange={(v) => setField('schoolInterestedId', v)}
                items={schools}
                placeholder={t('leads.form.schoolPlaceholder')}
                disabled={submitting}
              />
            </FormField>
            <FormField
              id="leadSource"
              label={t('leads.form.leadSource')}
              required
              error={fieldErrors.leadSource}
            >
              <select
                value={form.leadSource}
                disabled={submitting}
                onChange={(e) => setField('leadSource', e.target.value as LeadSource)}
              >
                <option value="" disabled>
                  {t('leads.form.leadSourcePlaceholder')}
                </option>
                {LEAD_SOURCES.map((source) => (
                  <option key={source} value={source}>
                    {t(`leads.source.${source}`)}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
        </section>

        {canAssignOnCreate && (
          <section className="fa-form-section">
            <h2 className="fa-form-section__title">{t('leads.form.assignmentSection')}</h2>
            <FormField
              id="assignedEmployee"
              label={t('leads.form.assignedEmployee')}
              error={fieldErrors.assignedEmployee}
              hint={t('leads.form.assignedEmployeeHint')}
            >
              <AgentCombobox
                id="assignedEmployee"
                value={form.assignedEmployee}
                disabled={submitting}
                onChange={(v) => setField('assignedEmployee', v)}
                placeholder={t('leads.form.assignedEmployeeSearch')}
                clearLabel={t('leads.form.assignedEmployeeUnassigned')}
              />
            </FormField>
          </section>
        )}

        <section className="fa-form-section">
          <h2 className="fa-form-section__title">{t('leads.form.noteSection')}</h2>
          <FormField id="note" label={t('leads.form.note')}>
            <textarea
              value={form.note}
              disabled={submitting}
              onChange={(e) => setField('note', e.target.value)}
            />
          </FormField>
        </section>

        <div className="fa-form-actions fa-form-actions--sticky">
          <button
            type="button"
            className="fa-btn fa-btn--secondary"
            disabled={submitting}
            onClick={() => navigate(-1)}
          >
            {t('leads.form.cancel')}
          </button>
          <button type="submit" className="fa-btn fa-btn--primary" disabled={submitting}>
            {submitting ? t('leads.form.submitting') : t('leads.form.submitCreate')}
          </button>
        </div>
      </form>
    </section>
  );
}
